import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  Button,
  Layout,
  Spinner,
  Tabs,
  ToastAlert,
} from '@design-systems/canvas-ui'
import Icon from '@design-systems/react-icons'
import { useNavigate } from 'react-router-dom'
import BrandProfileView from '../components/BrandProfileView'
import BrandReferenceLinks from '../components/BrandReferenceLinks'
import DeleteDocumentModal from '../components/DeleteDocumentModal'
import DocumentStatusView from '../components/DocumentStatusView'
import KnowledgeBaseView from '../components/KnowledgeBaseView'
import PublishQueueModal from '../components/PublishQueueModal'
import ReviewDraftsView from '../components/ReviewDraftsView'
import UploadDocumentsView from '../components/UploadDocumentsView'
import {
  BRAND_DETAILS_TABS,
  BRAND_PROFILE_SECTION_COUNT,
  DOCUMENT_STATUS,
  isDocumentUploaded,
  toDocumentTypeOptions,
} from '../config/brandDetailsConfig'
import {
  BRAND_LIFECYCLE_STATUS,
  EXTRACTION_ERROR_FALLBACK,
  getFurthestLifecycleStatus,
  hasExtractionFailed,
  hasSections,
  isActive,
  isDraft,
  isExtractionComplete,
  isExtractionPhase,
  isExtractionRunning,
  isReviewLocked,
  UNDO_SAFETY_MARGIN_SECONDS,
} from '../config/brandLifecycleConfig'
import { RAIL_STAGE } from '../config/documentRailConfig'
import { getRailStage } from '../helpers/documentRailHelpers'
import { formatStatus } from '../../allBrands/mappers/brandCatalogMapper'
import LifecycleStatusBadge from '../../../shared/brand/components/LifecycleStatusBadge'
import { canSendToPublishQueue } from '../helpers/draftReviewHelpers'
import { getDraftsScreen } from '../helpers/draftsScreenHelpers'
import { toExtractionProgressList } from '../helpers/extractionProgressHelpers'
import {
  hasPermission,
  PERMISSIONS,
  ROLES,
} from '../../../shared/roleAccess/config/roleConfig'
import { useCurrentUserRole } from '../../../shared/roleAccess/hooks/useCurrentUserRole'
import { useDocumentUploads } from '../hooks/useDocumentUploads'
import { getApiErrorMessage } from '../../../shared/api/apiClient'
import { useBrandProfile } from '../hooks/useBrandProfile'
import { useBrandPublish } from '../hooks/useBrandPublish'
import { useDraftReview } from '../hooks/useDraftReview'
import { useExtraction } from '../hooks/useExtraction'
import { useBrand } from '../../../shared/brand/context/BrandContext'
import { useBrandDetails } from '../context/BrandDetailsContext'
import {
  mapDocumentTypeStatuses,
  mapExtractionTypeStatuses,
} from '../mappers/brandDocumentMapper'
import './BrandDetailsPage.css'

const DRAFTS_TAB = 'drafts'
const PROFILE_TAB = 'profile'
const BRAND_MISMATCH_ERROR_CODE = 'DOCUMENT_BRAND_MISMATCH'

const formatBrandName = (brandSlug = '') =>
  brandSlug
    .split(/[_-]/)
    .filter(Boolean)
    .map((word) => `${word.charAt(0).toUpperCase()}${word.slice(1)}`)
    .join(' ')

const BrandDetailsPage = () => {
  const navigate = useNavigate()
  const {
    brand: loadedBrand,
    brandSlug,
    deleteDocument,
    documents: persistedDocuments,
    isDocumentsLoading,
    refreshBrand,
    refreshDocuments,
  } = useBrandDetails()
  const { activeDocumentTypes, getCategoryById } = useBrand()
  // Memoised because hooks below key their data loading off this object. A
  // fresh literal each render would restart those effects forever.
  const brand = useMemo(
    () => loadedBrand || { id: brandSlug },
    [brandSlug, loadedBrand],
  )
  const role = useCurrentUserRole()
  const documentTypes = useMemo(
    () => toDocumentTypeOptions(activeDocumentTypes),
    [activeDocumentTypes],
  )
  // Two readings of the same documents. The upload rail asks how far a type
  // has got at best; the drafts rail asks how far it has got at worst, so a
  // type with one file still extracting never looks ready to review.
  const documentStatuses = useMemo(
    () => mapDocumentTypeStatuses(documentTypes, persistedDocuments),
    [documentTypes, persistedDocuments],
  )
  const extractionStatuses = useMemo(
    () => mapExtractionTypeStatuses(documentTypes, persistedDocuments),
    [documentTypes, persistedDocuments],
  )
  const accessibleTabs = useMemo(
    () =>
      BRAND_DETAILS_TABS.filter(({ permission }) =>
        hasPermission(role, permission),
      ),
    [role],
  )
  // Unset until the brand loads, so the tab it opens on can follow its status.
  const [selectedTab, setSelectedTab] = useState(null)
  useEffect(() => {
    if (!loadedBrand) return
    const { lifecycleStatus: loadedStatus } = loadedBrand
    setSelectedTab((current) => {
      if (current) return current
      if (
        loadedStatus === BRAND_LIFECYCLE_STATUS.IN_REVIEW ||
        loadedStatus === BRAND_LIFECYCLE_STATUS.REVIEW_COMPLETED
      ) {
        return DRAFTS_TAB
      }
      return isActive(loadedStatus) ? PROFILE_TAB : 'upload'
    })
  }, [loadedBrand])
  // Set by the Review button on the extraction-progress screen. A brand that
  // is already IN_REVIEW has no such screen in front of its sections, so this
  // stays false there and `isReviewOpen` below is true anyway.
  const [hasOpenedReview, setHasOpenedReview] = useState(false)
  const [isPublishModalOpen, setIsPublishModalOpen] = useState(false)
  const [acceptedPublication, setAcceptedPublication] = useState(null)
  // The publication that can still be cancelled, and when that stops.
  const [undoPublication, setUndoPublication] = useState(null)
  const closeUndo = useCallback(() => setUndoPublication(null), [])
  // POST /extraction returns long before the brands list is re-read, so the
  // status this session just caused is held here until that catches up.
  // getFurthestLifecycleStatus drops it again the moment the server is ahead.
  const [startedLifecycleStatus, setStartedLifecycleStatus] = useState('')
  const [toast, setToast] = useState(null)
  const onDocumentSaved = useCallback(
    () => refreshDocuments().catch(() => {}),
    [refreshDocuments],
  )
  const { dismissUpload, queueUploads, retryUpload, uploadJobs } =
    useDocumentUploads({ brand, onDocumentSaved })

  const isProfileTab = selectedTab === PROFILE_TAB
  // The rail tracks documents and the publish queue, which the profile and
  // the Knowledge Base have no part in.
  const isRailHidden = isProfileTab || selectedTab === 'knowledge'
  // brand_lifecycle_status drives this whole screen: which face the Drafts
  // tab wears, whether the status endpoint is worth polling, and when the
  // sections are worth fetching.
  const lifecycleStatus = getFurthestLifecycleStatus(
    brand.lifecycleStatus,
    startedLifecycleStatus,
  )
  const hasAcceptedPublication = acceptedPublication?.brandId === brand.id
  const isReviewOpen = hasSections(lifecycleStatus) || hasOpenedReview
  // Polled only while extraction is the brand's current state — that is the
  // one window in which the answer can still change. `loadedBrand` gates it
  // too, because until that resolves `brand` holds the URL slug, which the
  // endpoint has no idea about.
  const { isStarting, startExtraction, status } = useExtraction({
    brand,
    // A DRAFT brand is read once too, to surface a failed earlier extraction.
    isEnabled:
      Boolean(loadedBrand) &&
      (isExtractionPhase(lifecycleStatus) || isDraft(lifecycleStatus)),
  })
  const isExtracting = isExtractionRunning(status.status)
  const extractionError = hasExtractionFailed(status.status)
    ? status.error || EXTRACTION_ERROR_FALLBACK
    : ''
  // Fetched as soon as the brand says the sections exist, whichever tab is
  // open: the rail's Send to Publish Queue needs them on every tab, and someone
  // arriving on an IN_REVIEW brand sees them without a detour through the
  // progress screen.
  const draftReview = useDraftReview({
    brand,
    isEnabled: Boolean(loadedBrand) && isReviewOpen,
  })
  // Only a live brand has a profile; before that the tab shows a prompt
  // instead and nothing is fetched.
  const brandProfile = useBrandProfile({
    brand,
    isEnabled:
      isProfileTab && Boolean(loadedBrand) && isActive(lifecycleStatus),
  })
  const { review } = draftReview
  const progressItems = useMemo(
    () =>
      toExtractionProgressList({
        documents: status.documents,
        documentTypes,
      }),
    [documentTypes, status.documents],
  )
  // Read alongside the sections: the brand can still say REVIEW_COMPLETED
  // after it has been sent, and only the current publication knows.
  const {
    cancelPublication,
    currentPublication,
    isCancelling,
    isCheckingPublication,
    isPublishing,
    isRetryExhausted,
    publicationCheckFailed,
    recoverPublication,
    retryPublication,
  } = useBrandPublish({
    brand,
    isEnabled:
      Boolean(loadedBrand) && isReviewOpen && !isActive(lifecycleStatus),
    onPublicationChange: refreshBrand,
  })
  // The rail reads optimistically while documents are still arriving, then
  // switches to the pessimistic rollup so a half-extracted type can never
  // present itself as ready to review.
  const railStatuses = isDraft(lifecycleStatus)
    ? documentStatuses
    : extractionStatuses
  // The stage decides which call to action the rail offers, and how an
  // uploaded document reads underneath each type.
  const railStage = getRailStage({
    documentStatuses: railStatuses,
    isPublicationAccepted: hasAcceptedPublication,
    isRetryAvailable: status.retryAvailable,
    isReviewOpen,
    lifecycleStatus,
    publication: currentPublication,
  })

  // What the Review button waits on. The brand carrying its own sections is
  // enough on its own, so a reload straight onto IN_REVIEW — where nothing is
  // polled — still offers the way in.
  const isReviewReady =
    isExtractionComplete(status.status) ||
    status.reviewReady ||
    hasSections(lifecycleStatus)

  // Announce the finish once, on the edge out of extraction. A job that ended
  // in failure or was cancelled has its own screen and must not be greeted as
  // a success, so this only fires when there are sections to go and look at.
  // The brand is deliberately left alone: the reviewer is still reading this
  // screen, and re-reading it would move them off it.
  const extractedCount = status.documents.length
  const wasExtracting = useRef(false)
  useEffect(() => {
    if (wasExtracting.current && !isExtracting && isReviewReady) {
      setToast({
        heading: `All ${extractedCount} document(s) extracted — review sections to approve`,
        variant: 'success',
      })
      refreshDocuments().catch(() => {})
    }
    wasExtracting.current = isExtracting
  }, [extractedCount, isExtracting, isReviewReady, refreshDocuments])

  const brandName = brand.name || formatBrandName(brandSlug) || 'Brand'
  const category = getCategoryById(brand.categoryId)
  // Nothing is shown until the brand has loaded and its status has picked the
  // tab, so the page never opens on one tab and jumps to another.
  const selectedTabConfig =
    selectedTab &&
    (accessibleTabs.find(({ id }) => id === selectedTab) || accessibleTabs[0])

  const runExtraction = async () => {
    // The API validates the documents before it starts, which can take a while.
    setToast({
      heading: 'Validation in progress',
      message: <Spinner size="sm" />,
      variant: 'info',
    })
    const { error, started } = await startExtraction()
    if (error) {
      setToast({ heading: error, variant: 'error' })
      return
    }
    setToast({
      heading: `AI extraction started for ${started.documentCount || persistedDocuments.length} document(s)`,
      variant: 'info',
    })
    setHasOpenedReview(false)
    setSelectedTab(DRAFTS_TAB)
    // The brand's own status only arrives with the next brands-list read, so
    // the progress screen would otherwise wait out a round trip before it
    // appeared. Hold the status this click caused, and ask for the real one.
    setStartedLifecycleStatus(BRAND_LIFECYCLE_STATUS.EXTRACTION_IN_PROGRESS)
    refreshBrand().catch(() => {})
  }

  const [deletingId, setDeletingId] = useState('')
  const removeDocument = async (documentId) => {
    setDeletingId(documentId)
    try {
      await deleteDocument(documentId)
      setToast({ heading: 'Document deleted', variant: 'success' })
      return true
    } catch (requestError) {
      setToast({
        heading: getApiErrorMessage(
          requestError,
          'The document could not be deleted.',
        ),
        variant: 'error',
      })
      return false
    } finally {
      setDeletingId('')
    }
  }

  // The previous extraction can have failed because a file belongs to another
  // brand. The Upload tab explains it in a banner, and the reviewer is sent
  // there once per job.
  const mismatchJobId =
    isDraft(lifecycleStatus) && status.errorCode === BRAND_MISMATCH_ERROR_CODE
      ? status.jobId
      : ''
  useEffect(() => {
    if (mismatchJobId) setSelectedTab('upload')
  }, [mismatchJobId])

  // A finished upload can be deleted from its progress row for as long as the
  // document is still UPLOADED.
  const deletableUploadJobs = uploadJobs.map((job) => {
    const document = persistedDocuments.find(
      ({ fileName, status }) =>
        status === DOCUMENT_STATUS.UPLOADED && fileName === job.fileName,
    )
    return job.status === DOCUMENT_STATUS.UPLOADED && document
      ? { ...job, documentId: document.id }
      : job
  })
  // Both delete icons only ask; the call is made once the reviewer confirms.
  const [pendingDelete, setPendingDelete] = useState(null)
  const requestDelete = (document) => setPendingDelete(document)
  const confirmDelete = async () => {
    const { id, jobId } = pendingDelete
    if ((await removeDocument(id)) && jobId) dismissUpload(jobId)
    setPendingDelete(null)
  }

  const confirmPublishQueue = async () => {
    const publication = await draftReview.sendToPublishQueue()
    setIsPublishModalOpen(false)
    if (!publication) return
    setAcceptedPublication({ brandId: brand.id })
    const undoSeconds =
      publication.undo_seconds_remaining - UNDO_SAFETY_MARGIN_SECONDS
    if (undoSeconds > 0) {
      setUndoPublication({
        endsAt: Date.now() + undoSeconds * 1000,
        id: publication.publication_id,
      })
    }
    // The rail shows the queue straight away; the read-only mode only catches
    // up once the brand is re-read.
    refreshBrand().catch(() => {})
  }

  // Back to the review, as if the publication had never been sent.
  const cancelPublish = async () => {
    const { error } = await cancelPublication(undoPublication.id)
    setUndoPublication(null)
    setToast(
      error
        ? { heading: error, variant: 'error' }
        : { heading: 'Publishing cancelled', variant: 'success' },
    )
    if (error) return
    setAcceptedPublication(null)
    refreshBrand().catch(() => {})
  }

  const runRecover = async () => {
    const { error } = await recoverPublication()
    setToast(
      error
        ? { heading: error, variant: 'error' }
        : { heading: 'Publication recovered', variant: 'success' },
    )
    refreshBrand().catch(() => {})
  }

  // Republish: sends the failed publication again. Re-reading the brand also
  // re-reads the publication, which is what moves the rail on.
  const runPublish = async () => {
    const { error } = await retryPublication()
    setToast(
      error
        ? { heading: error, variant: 'error' }
        : { heading: `Publishing ${brandName}`, variant: 'success' },
    )
    refreshBrand().catch(() => {})
  }

  // Resolves to whether the save went through, so the profile leaves edit
  // mode only on success.
  const saveProfile = async (sections) => {
    const error = await brandProfile.saveSections(sections)
    setToast(
      error
        ? { heading: error, variant: 'error' }
        : { heading: 'Brand profile sections are updated', variant: 'success' },
    )
    return !error
  }

  // The rail offers Review from any tab, so it carries the reviewer over to
  // the drafts tab rather than opening a screen they cannot see.
  const openReview = () => {
    setSelectedTab(DRAFTS_TAB)
    setHasOpenedReview(true)
  }

  const renderSelectedTab = () => {
    switch (selectedTabConfig?.id) {
      case DRAFTS_TAB:
        return (
          <ReviewDraftsView
            extractionError={extractionError}
            isReadOnly={isReviewLocked(lifecycleStatus, currentPublication)}
            // Until the first status response lands there is nothing to say
            // the job has finished, so the screen opens as still working.
            isExtracting={isExtracting || isStarting || !isReviewReady}
            isSaving={draftReview.isSaving}
            onApproveGroup={draftReview.approveGroup}
            onEditApprovedGroup={draftReview.editApprovedGroup}
            onEditGroup={draftReview.editGroup}
            onResolveSection={draftReview.resolveSection}
            onRetryExtraction={runExtraction}
            onUndoGroup={draftReview.undoGroup}
            progressItems={progressItems}
            review={review}
            reviewError={draftReview.error}
            screen={getDraftsScreen({
              isReviewLoading: draftReview.isLoading,
              isReviewOpen,
              lifecycleStatus,
            })}
          />
        )
      case 'knowledge':
        return (
          <KnowledgeBaseView
            documents={persistedDocuments}
            isLoading={isDocumentsLoading}
          />
        )
      case PROFILE_TAB:
        return (
          <BrandProfileView
            canEdit={hasPermission(role, PERMISSIONS.EDIT_BRAND_PROFILE)}
            error={brandProfile.error}
            isLoading={brandProfile.isLoading}
            isSaving={brandProfile.isSaving}
            lifecycleStatus={lifecycleStatus}
            onOpenReview={openReview}
            onSave={saveProfile}
            onStartUploading={() => setSelectedTab('upload')}
            profile={brandProfile.profile}
            requiredDocumentLabels={documentTypes
              .filter(({ required }) => required)
              .map(({ label }) => label)}
          />
        )
      case 'assets':
      case 'activity':
        return (
          <section
            aria-label={selectedTabConfig.label}
            className="brand-details-empty-panel"
          />
        )
      default:
        return (
          <UploadDocumentsView
            mismatchError={mismatchJobId ? status.error : ''}
            lifecycleStatus={lifecycleStatus}
            documentTypes={documentTypes}
            existingFileNames={persistedDocuments.map(
              ({ fileName }) => fileName,
            )}
            deletingId={deletingId}
            onDeleteDocument={({ documentId, fileName, id }) =>
              requestDelete({ fileName, id: documentId, jobId: id })
            }
            onDismissUpload={dismissUpload}
            onQueueUploads={queueUploads}
            onRetryUpload={retryUpload}
            uploadJobs={deletableUploadJobs}
            onOpenReview={openReview}
          />
        )
    }
  }

  if (!hasPermission(role, PERMISSIONS.VIEW_BRAND_DETAILS)) {
    return (
      <p className="brand-details__error" role="alert">
        You do not have access to view this brand.
      </p>
    )
  }

  return (
    <section className="brand-details">
      {toast && (
        <ToastAlert
          autoHideDuration={6000}
          className="brand-details__toast"
          heading={toast.heading}
          location="top"
          message={toast.message}
          onComplete={() => setToast(null)}
          onRefuse={() => setToast(null)}
          variant={toast.variant}
        />
      )}
      <Layout.PageHeader className="brand-details-header" isSticky>
        <div className="brand-details-header__overview">
          <div className="brand-details-header__title">
            <h1>
              {brandName} <span>{category?.label || category?.name}</span>
              {/* A queued brand can still say REVIEW_COMPLETED, so the rail's
                  view of the publication decides this one. */}
              {lifecycleStatus && (
                <LifecycleStatusBadge
                  className="brand-details-header__chip"
                  fallbackLabel={formatStatus(lifecycleStatus)}
                  status={
                    railStage === RAIL_STAGE.QUEUED
                      ? BRAND_LIFECYCLE_STATUS.AWAITING_PUBLISH
                      : lifecycleStatus
                  }
                />
              )}
            </h1>
            {/* Section counts come from the sections API summary, so the
                header, the review screen and every write response all report
                the same numbers. The document counter is a different thing —
                how much of the required upload set has arrived — and stays
                local, because it has to answer before extraction exists. */}
            <p>
              {review.approvedCount} /{' '}
              {review.totalSectionCount || BRAND_PROFILE_SECTION_COUNT} sections
              approved ·{' '}
              {
                documentStatuses.filter(
                  ({ required, status }) =>
                    required && isDocumentUploaded(status),
                ).length
              }{' '}
              of {documentStatuses.filter(({ required }) => required).length}{' '}
              required docs
            </p>
          </div>
          <Button
            className="brand-details-header__ask-button"
            onClick={() =>
              navigate(
                `/assistant?brand=${encodeURIComponent(brand.slug || brandSlug)}`,
              )
            }
            variant="primary"
          >
            Ask this brand
          </Button>
        </div>
        <div className="brand-details-header__navigation">
          <Tabs
            activeTab={selectedTabConfig?.id}
            className="brand-details-tabs"
            disableAdaptive
            onTabSelect={setSelectedTab}
            spacing="none"
          >
            {accessibleTabs.map((tab) => (
              <Tabs.Item key={tab.id} name={tab.id}>
                <span className="brand-details-tabs__label">
                  <Icon size="sm" svg={tab.icon} /> {tab.label}
                  {tab.id === DRAFTS_TAB && review.sectionCount > 0 && (
                    <span className="brand-details-tabs__badge">
                      {review.sectionCount}
                    </span>
                  )}
                </span>
              </Tabs.Item>
            ))}
          </Tabs>
          <BrandReferenceLinks brand={brand} role={role} />
        </div>
      </Layout.PageHeader>
      <div
        className={`brand-details__layout${
          isRailHidden ? ' brand-details__layout--full' : ''
        }`}
      >
        <main className="brand-details__content">
          {selectedTabConfig && renderSelectedTab()}
        </main>
        {!isRailHidden && (
          <DocumentStatusView
            canRecover={role === ROLES.ADMIN}
            canSendToPublishQueue={canSendToPublishQueue(review.groups)}
            deletingId={deletingId}
            documentStatuses={railStatuses}
            isCancellingPublish={isCancelling}
            isRetryExhausted={isRetryExhausted}
            isCheckingPublication={isCheckingPublication}
            isDraftBrand={isDraft(lifecycleStatus)}
            isExtracting={isStarting || isExtracting}
            isPublishing={isPublishing}
            isRetryAvailable={status.retryAvailable}
            isReviewReady={isReviewReady}
            isSending={draftReview.isSending}
            onCancelPublish={cancelPublish}
            onDeleteDocument={(document) =>
              requestDelete({ fileName: document.fileName, id: document.id })
            }
            onOpenReview={openReview}
            onPublishBrand={runPublish}
            onRecoverPublication={runRecover}
            onRunExtraction={runExtraction}
            onSendToPublishQueue={() => setIsPublishModalOpen(true)}
            onUndoExpire={closeUndo}
            publicationCheckFailed={publicationCheckFailed}
            stage={railStage}
            undoEndsAt={undoPublication?.endsAt}
          />
        )}
      </div>
      <DeleteDocumentModal
        fileName={pendingDelete?.fileName}
        isDeleting={Boolean(deletingId)}
        isOpen={Boolean(pendingDelete)}
        onClose={() => setPendingDelete(null)}
        onConfirm={confirmDelete}
      />
      <PublishQueueModal
        approvedCount={review.approvedCount}
        isOpen={isPublishModalOpen}
        isSending={draftReview.isSending}
        onClose={() => setIsPublishModalOpen(false)}
        onConfirm={confirmPublishQueue}
      />
    </section>
  )
}

export default BrandDetailsPage
